import createStandardTrackingEvents from "../utils/trackingEvents";

const DEFAULT_ENDPOINT =
    process.env.VUE_APP_TRACKER_ENDPOINT ||
    "https://sdkapi.rcbt-sip.com/api/events";

const DEFAULT_API_KEY =
    process.env.VUE_APP_TRACKER_API_KEY ||
    "fe0066dc3bf49a40b1aca44a1e2295e40e9d534a24409984b7b1d407f3cac8c6";
const DEFAULT_INDEX = "RCBT-SIP";

const isMobileDevice = () =>
    /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

const generateEventId = () => {
    const random = Math.random().toString(36).slice(2, 10);
    return `evt-${Date.now()}-${random}`;
};

const toNumberOrDefault = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const getAccount = () => localStorage.getItem("id") || "Guest";

const getCachedGeoData = () => {
    try {
        const raw = localStorage.getItem("geoData");
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
};

const buildPayload = (event = {}) => {
    const geoData = getCachedGeoData() || {};
    const custom = event.customValue || {};
    const nowIso = new Date().toISOString();

    return {
        _id: event._id || generateEventId(),
        _index: event._index || DEFAULT_INDEX,
        name: event.name || "click",
        category: event.category || "app",
        customValue: {
            datetime: custom.datetime || nowIso,
            account: custom.account || getAccount(),
            serviceId: toNumberOrDefault(custom.serviceId, 0),
            serviceName:
                custom.serviceName || document.title || "Unknown Service",
            serviceType: custom.serviceType || "web",
            keyword: custom.keyword || "",
            locLat: toNumberOrDefault(custom.locLat, 0),
            locLon: toNumberOrDefault(custom.locLon, 0),
            serviceIp: custom.serviceIp || geoData.IPv4 || "",
            device: custom.device || (isMobileDevice() ? "mobile" : "desktop"),
            operation: custom.operation || "view",
        },
    };
};

const sendManualEvent = async (event = {}, config = {}) => {
    const endpoint = config.endpoint || DEFAULT_ENDPOINT;
    const apiKey = config.apiKey || DEFAULT_API_KEY;
    const payload = buildPayload(event);

    const response = await fetch(endpoint, {
        method: "POST",
        headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            "x-api-key": apiKey,
        },
        body: JSON.stringify(payload),
        keepalive: true,
    });

    if (!response.ok) {
        const message = await response.text();
        throw new Error(`Tracker API ${response.status}: ${message}`);
    }

    return response.json();
};

const createClickEventFromDom = (domEvent) => {
    const target = domEvent?.target;
    const elementTag = target?.tagName?.toLowerCase() || "unknown";
    const text = (target?.textContent || "").trim();

    return {
        name: "click",
        category: "interaction",
        customValue: {
            serviceType: elementTag,
            serviceName: text || document.title || "Unknown Service",
            operation: "click",
        },
    };
};

const ManualTrackerPlugin = {
    install(app, options = {}) {
        const trackerConfig = {
            endpoint: options.endpoint || DEFAULT_ENDPOINT,
            apiKey: options.apiKey || DEFAULT_API_KEY,
        };

        const $sendManualEvent = (event = {}) =>
            sendManualEvent(event, trackerConfig);

        const $trackClickEvent = (domEvent, overrides = {}) =>
            $sendManualEvent({
                ...createClickEventFromDom(domEvent),
                ...overrides,
                customValue: {
                    ...createClickEventFromDom(domEvent).customValue,
                    ...(overrides.customValue || {}),
                },
            });

        const standardEvents = createStandardTrackingEvents($sendManualEvent);
        const $trackSearchSubmit = standardEvents.trackSearchSubmit;
        const $trackProductView = standardEvents.trackProductView;
        const $trackContactSubmit = standardEvents.trackContactSubmit;

        app.config.globalProperties.$sendManualEvent = $sendManualEvent;
        app.config.globalProperties.$trackClickEvent = $trackClickEvent;
        app.config.globalProperties.$trackSearchSubmit = $trackSearchSubmit;
        app.config.globalProperties.$trackProductView = $trackProductView;
        app.config.globalProperties.$trackContactSubmit = $trackContactSubmit;
        app.config.globalProperties.$trackStandardEvents = standardEvents;

        app.provide("sendManualEvent", $sendManualEvent);
        app.provide("trackClickEvent", $trackClickEvent);
        app.provide("trackSearchSubmit", $trackSearchSubmit);
        app.provide("trackProductView", $trackProductView);
        app.provide("trackContactSubmit", $trackContactSubmit);
        app.provide("trackStandardEvents", standardEvents);

        if (options.router && typeof options.router.afterEach === "function") {
            options.router.afterEach((to) => {
                $sendManualEvent({
                    name: "page_view",
                    category: "navigation",
                    customValue: {
                        serviceName: to.name || to.path || document.title,
                        serviceType: "route",
                        operation: "view",
                    },
                }).catch((error) => {
                    if (process.env.NODE_ENV !== "production") {
                        console.error(
                            "Manual tracker page_view failed:",
                            error,
                        );
                    }
                });
            });
        }
    },
};

export { sendManualEvent, buildPayload };
export default ManualTrackerPlugin;
